import React from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../../theme/ThemeProvider";
import {buildStyles} from "../../buildStyles";
import Container from "../../layout/Container";
import FlexContainer from "../../layout/FlexContainer";
import RadioButton from "../../input/RadioButton";
import OutlinedCardBase from "./base/OutlinedCardBase";
import {generateStyles} from "../../../theme/styleUtilities";

const useStyles = (theme) =>
  buildStyles({
    radioContainer: {
      position: "absolute",
      top: theme.spacing(4),
      right: theme.spacing(4),
    },
    content: {
      width: "100%",
      paddingRight: theme.spacing(6), // Add padding to avoid overlap with radio button
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: theme.spacing(8),
      height: theme.spacing(8),
      borderRadius: "50%",
      backgroundColor: theme.colors.primary100,
      color: theme.colors.primary,
      marginBottom: theme.spacing(3),
    },
    actionRow: {
      display: "flex",
      justifyContent: "flex-start",
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
      width: "100%",
    },
  });

/**
 * CardToggle extends the card base pattern with additional toggle functionality via a RadioButton.
 * It supports custom cardBase and all the standard card content slots.
 * This component maintains backward compatibility with the existing CardToggle API.
 *
 * @param {Object} props - The props for the CardToggle component.
 * @param {React.ElementType} [props.cardBase=OutlinedCardBase] - The component to render for the card base.
 * @param {React.ReactNode} [props.icon] - The icon to display in the card.
 * @param {React.ReactNode} [props.title] - The title text for the card.
 * @param {React.ReactNode} [props.description] - The description text for the card.
 * @param {React.ReactNode} [props.inlineContent] - Additional inline content for the card.
 * @param {React.ReactNode} [props.actionRow] - Optional action row, e.g., buttons or links.
 * @param {React.ReactNode} [props.customContent] - Fully overrides default behavior and renders custom content inside the card.
 * @param {boolean} [props.isSelected=false] - Whether the toggle card is selected.
 * @param {function} [props.onToggle] - Callback when the toggle is changed.
 * @param {Object} [props.sx={}] - Style overrides for the card container.
 * @param {Object} props.rest - Any other props that should be passed to the card base component.
 *
 * @returns {JSX.Element} The rendered CardToggle component.
 */
const CardToggle = ({
  cardBase: CardBaseComponent = OutlinedCardBase,
  icon = null,
  title = null,
  description = null,
  inlineContent = null,
  actionRow = null,
  customContent = null,
  isSelected = false,
  onToggle,
  sx = {},
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  const defaultStyles = {
    position: "relative",
    cursor: "pointer",
    border: `1px solid ${
      isSelected ? theme.colors.primary : theme.colors.gray300
    }`,
    "&:hover": {
      borderColor: theme.colors.primary,
    },
  };

  const styles = generateStyles(
    {sx: {...defaultStyles, ...sx}, ...props},
    theme,
  );

  const handleClick = () => {
    if (onToggle) {
      onToggle(!isSelected);
    }
  };

  return (
    <CardBaseComponent sx={styles} onClick={handleClick} {...props}>
      {customContent || (
        <Container className={classes.content}>
          {icon && (
            <Container className={classes.iconContainer}>{icon}</Container>
          )}

          {title && (
            <Container
              sx={{textAlign: "left", display: "block", width: "100%"}}
            >
              {title}
            </Container>
          )}

          {description && (
            <Container
              sx={{
                textAlign: "left",
                display: "block",
                width: "100%",
                marginTop: title ? theme.spacing(1) : 0,
              }}
            >
              {description}
            </Container>
          )}

          {inlineContent && (
            <Container
              sx={{
                textAlign: "left",
                display: "block",
                width: "100%",
                marginTop: title || description ? theme.spacing(2) : 0,
              }}
            >
              {inlineContent}
            </Container>
          )}

          {actionRow && (
            <Container className={classes.actionRow}>{actionRow}</Container>
          )}
        </Container>
      )}

      <Container className={classes.radioContainer}>
        <RadioButton checked={isSelected} onChange={handleClick} />
      </Container>
    </CardBaseComponent>
  );
};

CardToggle.propTypes = {
  cardBase: PropTypes.any,
  icon: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  inlineContent: PropTypes.node,
  actionRow: PropTypes.node,
  customContent: PropTypes.node,
  isSelected: PropTypes.bool,
  onToggle: PropTypes.func,
  sx: PropTypes.object,
};

export default CardToggle;
